import { Button, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { DateTime } from 'luxon';
import DeleteIcon from '@mui/icons-material/Delete';
import TabPanel from "../../components/TabPanel";
import { TransactionModel, RequestResult, BlobDocumentType } from "../../gql-types.generated";
import { setToastConfig } from '../EDIContainer/EDIContainerSlice';
import { CountBox, DetailsTabContainer, DetailsTabHeader, DetailsTabHeaderWrapper, DetailsTabPanelContainer, TabHeader, TabLabel } from "../../util/SharedStyles";
import { fetchTransactionMapById, deleteMapData, fetchTransactionMapPartners } from "./MapDetailsActions";
import { clearState, selectError, clearError, selectMap, selectDeleteMapStatus, captureDeleteMapStatus, selectMapPartners } from "./MapDetailsSlice";
import { Viewer, ToastSeverity } from '../../util/Constants';
import { getLocalStorageItem, setLocalStorageItem, viewerCanEdit, viewerIsAdmin } from '../../util/ViewerUtility';
import { useTitle } from "../../util/Common";
import { getFormattedDateTimeString } from "../../util/DateTimeUtility";
import EditButton from '../../components/buttons/EditButton';
import DeleteDialog from '../../components/dialogs/DeleteDialog';
import TransactionMapPartnerList from "../../components/lists/TransactionMapPartnersList";
import BlobFileVersionList from "../../components/lists/BlobFileVersionList";
import { updateMapData } from "../Maps/MapsActions";
import { captureUpdateMapStatus, selectUpdateMapStatus } from "../Maps/MapsSlice";
import DetailsPage from '../../components/DetailsPage';
import EditMapDialog from "../../components/dialogs/EditMapDialog";

interface MapDetailsProps {
    viewer: Viewer | undefined;
}

const MapDetails: React.FC<MapDetailsProps> = (props) => {
    //mapIs should be passed through router
    const { mapId } = useParams();
    const { viewer } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [tabValue, setTabValue] = useState(0);
    const [openDelete, setOpenDelete] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const [createdBy, setCreatedBy] = useState<string | undefined>(undefined);
    const [createdTime, setCreatedTime] = useState<string | undefined>(undefined);
    const [description, setDescription] = useState<string | undefined>(undefined);
    const [lastModifiedBy, setLastModifiedBy] = useState<string | undefined>(undefined);
    const [lastModifiedTime, setLastModifiedTime] = useState<string | undefined>(undefined);
    const [standardVersion, setStandardVersion] = useState<string | undefined>(undefined);
    const [sourceFileName, setSourceFileName] = useState<string | undefined>(undefined);
    const [blobDocumentId, setBlobDocumentId] = useState<string | undefined>(undefined);
    const [totalPartners, setTotalPartners] = useState<number | undefined>(undefined);
    const [totalVersions, setTotalVersions] = useState<number | undefined>(undefined);
    const [transaction, setTransaction] = useState<TransactionModel | undefined>(undefined);
    
    const mapInfo = useAppSelector(selectMap);
    const deleteMapStatus = useAppSelector(selectDeleteMapStatus);
    const mapPartners = useAppSelector(selectMapPartners);
    const updateMapStatus = useAppSelector(selectUpdateMapStatus);
    const error = useAppSelector(selectError);

    const [verticalAlign, setVerticalAlign] = useState<boolean>(false);
    // detailsPageProps
    const [detailsRecordLoaded, setDetailsRecordLoaded] = useState<boolean>(false);
    const [detailsPageTitle, setDetailsPageTitle] = useState<string | undefined>(undefined);
    const [detailsHeader, setDetailsHeader] = useState<string | undefined>(undefined);
    const [detailsSubHeader, setDetailsSubHeader] = useState<string | undefined>(undefined);
    const [detailsToolbarButtons, setDetailsToolbarButtons] = useState<JSX.Element[] | undefined>(undefined);
    const [detailsEditButton, setDetailsEditButton] = useState<JSX.Element | undefined>(undefined);
    
    const canEdit = viewerCanEdit(viewer);
    const canDelete = viewerIsAdmin(viewer);

    const localStorageTabIndexName = "TransactionMapDetailsTabIndex_" + mapId;

    useEffect(() => {
        // get Map on page entry with passed in mapId
        if (mapId) {
            dispatch(fetchTransactionMapById(mapId));
            dispatch(fetchTransactionMapPartners(mapId));
        }
        // on page exit, set state to defaults (undefined)
        return () => {
            dispatch(clearState());
        }
    }, []);

    useEffect(() => {
        // refresh client when a successful mutation occurs
        if (updateMapStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: updateMapStatus.message as string,
                severity: ToastSeverity.Success
            }));
            // remove upsert status
            dispatch(captureUpdateMapStatus());
            // close the modify dialog
            onCloseEdit();
            // we should always have the mapId if we updated, but check to prevent compile error
            if (mapId) {
                dispatch(fetchTransactionMapById(mapId));
            }
        }
    }, [updateMapStatus?.result]);

    useEffect(() => {
        if (deleteMapStatus?.result === RequestResult.Success) {
            // close the delete dialog
            onDeleteDialogClose();
            dispatch(setToastConfig({
                message: deleteMapStatus.message as string,
                severity: ToastSeverity.Success
            }));
            // send user back to where they came from
            navigate(-1);

        }
        else if (deleteMapStatus?.result === RequestResult.Fail) {
            setDeleteErrorMessage(deleteMapStatus.message as string);
        }
    }, [deleteMapStatus?.result]);

    useEffect(() => {
        if (mapInfo) {
            const activeMapFileVersion = mapInfo.activeVersionBlobStorageReference?.blobStorage;
            //set field data
            if (mapInfo.createdByName) {
                setCreatedBy(mapInfo.createdByName);
            }
            if (mapInfo.createdTime) {
                let formattedDate = getFormattedDateTimeString(mapInfo.createdTime, { format: DateTime.DATETIME_FULL });
                setCreatedTime(formattedDate);
            }
            if (mapInfo.blobDocumentId) {
                setBlobDocumentId(mapInfo.blobDocumentId);
            }
            if (mapInfo.description) {
                setDescription(mapInfo.description);
            }
            
            if (mapInfo.lastModifiedByName) {
                setLastModifiedBy(mapInfo.lastModifiedByName);
            }
            if (mapInfo.lastModifiedTime) {
                let formattedDate = getFormattedDateTimeString(mapInfo.lastModifiedTime, { format: DateTime.DATETIME_FULL });
                setLastModifiedTime(formattedDate);
            }
            setSourceFileName(activeMapFileVersion?.fileName ?? undefined);
            if (mapInfo.standardVersion) {
                setStandardVersion(mapInfo.standardVersion);
            }
            if (mapInfo.totalVersions) {
                setTotalVersions(mapInfo.totalVersions);
            }
            if (mapInfo.transaction) {
                setTransaction(mapInfo.transaction);
            }
            

            // restore active tab to be last accessed tab that was saved off
            let tabIndex = getLocalStorageItem(viewer, localStorageTabIndexName);
            if (tabIndex != null && +tabIndex >= 0) {
                setTabValue(+tabIndex);
            }
        }
    }, [mapInfo]);

    useEffect(() => {
        // if partners data changes while on the page, re-evaluate if delete button should be shown or not based on if there are now partners associated to the map
        setTotalPartners(mapPartners?.length);
    }, [mapPartners]);

    useTitle(description);

    const updateAlignment = (verticalAlign: boolean) => {
        setVerticalAlign(verticalAlign);
    };

    const onEditClick = () => {
        setOpenEdit(true);
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setLocalStorageItem(viewer, localStorageTabIndexName, newValue.toString());
    };

    const onDeleteMap = () => {
        dispatch(captureDeleteMapStatus());
        setDeleteErrorMessage('');
        setOpenDelete(true);
    };

    const handleDeleteMapConfirm = () => {
        if (mapId) {
            dispatch(deleteMapData(mapId));
        }
    };

    const onDeleteDialogClose = () => {
        setOpenDelete(false);
        onDialogClose();
        dispatch(captureDeleteMapStatus());
        setDeleteErrorMessage('');
    };

    const onCloseEdit = () => {
        setOpenEdit(false);
        onDialogClose();
    };

    const onSaveEdit = (
        transactionId: string,
        description: string,
        standardVersion?: string
    ) => {
        dispatch(updateMapData({transactionId, description, standardVersion, id: mapId as string}));
    };

    const onDialogClose = () => {
        // Clear error and selectedMap on close.
        dispatch(clearError());
    };
    
    const refreshPartners = () => {
        if (mapId) {
            dispatch(fetchTransactionMapPartners(mapId));
        }
    }


    const getMapDetails = () => {
        if (mapInfo) {
            return (
                <Grid container alignItems="flex-start">
                    <Grid container spacing={2} columns={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }}>
                        <Grid item xs={1}>
                            <Typography variant='caption' aria-label='map file'>Map File Name</Typography>
                            <Typography variant='body1' >{sourceFileName}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant='caption' aria-label='standard version'>EDI Standard Version</Typography>
                            <Typography variant='body1' >{standardVersion}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant='caption' aria-label='date created'>Date Created</Typography>
                            <Typography variant='body1' >{createdTime}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant='caption' aria-label='created by'>Created By</Typography>
                            <Typography variant='body1' >{createdBy}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant='caption' aria-label='last updated'>Last Updated</Typography>
                            <Typography variant='body1' >{lastModifiedTime}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant='caption' aria-label='updated by'>Updated By</Typography>
                            <Typography variant='body1' >{lastModifiedBy}</Typography>
                        </Grid>

                    </Grid>
                </Grid>
            );
        }
    }

    let partnersLabel = (
        <Grid container item gap={"4px"} alignItems="center">
            <TabLabel>Partners</TabLabel>
            {mapPartners && mapPartners.length > 0 && (
                <CountBox>{mapPartners.length}</CountBox>
            )}
        </Grid>
    );

    const getTabbedContent = () => {
        if (mapInfo) {
            return (
                <DetailsTabContainer>
                    <DetailsTabHeaderWrapper>
                        <DetailsTabHeader value={tabValue} onChange={handleTabChange} >
                            <TabHeader label={partnersLabel} id='vertical-tab-0' />
                            <TabHeader label="Versions" id='vertical-tab-1' />
                        </DetailsTabHeader>
                    </DetailsTabHeaderWrapper>
                    <DetailsTabPanelContainer>
                        <TabPanel verticalAlign={verticalAlign} value={tabValue} index={0}>
                            <TransactionMapPartnerList 
                                viewer={viewer} 
                                mapId={mapId as string} 
                                mapPartners={mapPartners} 
                                refreshPartners={refreshPartners} />
                        </TabPanel>
                        <TabPanel verticalAlign={verticalAlign} value={tabValue} index={1}>
                            {blobDocumentId && (
                                <BlobFileVersionList
                                    viewerRole={viewer?.role}
                                    blobDocumentId={blobDocumentId}
                                    blobDocumentType={BlobDocumentType.TransactionMap}
                                    acceptedFileTypes={{ 'text/xml': ['.xslt', '.xsl'] }}
                                    fileTypeLabel="Map File"
                                    allowedExtensions={['.xslt', '.xsl']}
                                />
                            )}
                        </TabPanel>
                    </DetailsTabPanelContainer>
                </DetailsTabContainer>
            );
        }
    };

    const isDeleteVisible = canDelete && (totalPartners === undefined || totalPartners === 0) && totalVersions === 1;
       
    const getDialogs = () => [
        <DeleteDialog
            isOpen={openDelete}
            heading={'Delete Transaction Map'}
            message={`Are you sure you want to delete '${description}'?`}
            id={mapInfo?.id as string}
            onConfirm={handleDeleteMapConfirm}
            onReject={onDeleteDialogClose}
            errorMessage={deleteErrorMessage}
            key="delete-dialog"
        />,
        <EditMapDialog
            isOpen={openEdit}
            map={mapInfo}
            onClose={onCloseEdit}
            onSave={onSaveEdit}
            error={error}
            key="map-dialog"
        />
    ]

    useEffect(() => {
        setDetailsRecordLoaded(!!mapInfo);
        if (mapInfo) {
            setDetailsPageTitle(description);
            setDetailsHeader(description);
            setDetailsSubHeader(transaction?.name);
            if (isDeleteVisible) {
                setDetailsToolbarButtons([<Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDeleteMap}
                    key="delete-button"
                >
                    Delete</Button>]);
            } else {
                setDetailsToolbarButtons(undefined);
            }
            if (canEdit) {
                setDetailsEditButton(<EditButton
                    onClick={onEditClick}
                    ariaLabel="Edit Map button"
                    data-cy="edit-map-button"
                />);
            } else {
                setDetailsEditButton(undefined);    
            }
        }
    }, [mapInfo, description, transaction, canEdit, isDeleteVisible]);

    return (
        <DetailsPage
            recordLoaded={detailsRecordLoaded}
            pageTitle={detailsPageTitle}
            toolBarButtons={detailsToolbarButtons}
            detailsAV={undefined}
            detailsHeader={detailsHeader}
            detailsSubHeader={detailsSubHeader}
            detailsChips={undefined}
            detailsEditButton={detailsEditButton}
            detailsFavoriteButton={undefined}
            getDetailsListContainer={getMapDetails}
            getTabbedContainer={getTabbedContent}
            getDialogs={getDialogs}
            error={error}
            updateAlignment={updateAlignment}
        />
    );
}

export default MapDetails;